import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useAlert } from '@hooks/useAlert'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useBookAction } from '@hooks/book/useBookAction'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { PenNameByUserResponse } from '@models/penName/PenNameByUserResponse'
import { PenNameFormType } from '@models/penName/PenNameFormType'
import { PenNameListType } from '@models/penName/PenNameListType'
import { useTab } from '@components/Tabs/Tabs'
import { scrollToElement } from '@lib/utils'
import { usePenNameAction } from '@hooks/penName/usePenNameAction'
import { ProfilePenNameProps } from './interface'

export function withProfilePenName(Component: React.FC<ProfilePenNameProps>) {
  function WithProfilePenName() {
    const bookClient = useBookAction()
    const queryClient = useQueryClient()
    const alert = useAlert()
    const { user } = useAuthentication()
    const { page, perpage, pageChange } = usePagination()
    const { handleTabNameChange } = useTab()
    const [searchText, setSearchText] = useState('')
    const queryKey = ['my-pen-names', user.id, searchText, page, perpage]
    const penNameClient = usePenNameAction()

    const errorPenNameModal = useModal({
      modal: 'errorPenName',
    })

    const addPenNameModal = useModal(
      {
        modal: 'addPenName',
      },
      true
    )

    const editPenNameModal = useModal(
      {
        modal: 'editPenName',
      },
      true
    )

    const deletePenNameModal = useModal(
      {
        modal: 'deletePenName',
      },
      true
    )

    const {
      data: penName,
      refetch,
      isLoading,
    } = useQuery(
      queryKey,
      () => {
        return penNameClient.getPenNameByUserId({
          id: user.id,
          searchText,
          page,
          perpage,
        })
      },
      {
        enabled: !!user.id,
      }
    )

    function handleSearchChange(value: string) {
      pageChange(1)
      setSearchText(value)
    }

    function handleAddPenName() {
      addPenNameModal.show({
        penNameList: penName?.data?.map(row => row.firstPenName) ?? [],
        onSubmit: async (form: PenNameFormType) => {
          try {
            await penNameClient.createPenName(form)
            alert.success('เพิ่มนามปากกาสำเร็จ')
            await refetch()
            await queryClient.refetchQueries([
              'pen-name-options-by-user',
              user.id,
            ])
          } catch (_) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถเพิ่มนามปากกาได้')
          } finally {
            addPenNameModal.hide()
          }
        },
      })
    }

    function handleEditPenName(data: PenNameListType) {
      editPenNameModal.show({
        initialValues: {
          id: data.id,
          primaryPenName: data.firstPenName,
          secondaryPenName: data.secondPenName,
          fbUrl: data.facebookLink,
          twUrl: data.twitterLink,
        },
        penNameList: penName?.data
          ?.filter(row => row.id !== data.id)
          .map(row => row.firstPenName),
        onSubmit: async (form: PenNameFormType) => {
          try {
            const updateDate = await penNameClient.editPenName(form)

            if (updateDate) {
              queryClient.setQueryData<PenNameByUserResponse | undefined>(
                queryKey,
                (oldData?: PenNameByUserResponse) => {
                  if (oldData) {
                    const dataClone = cloneDeep(oldData)
                    const penNameIndex = dataClone.data.findIndex(
                      item => item.id === updateDate.id
                    )
                    dataClone.data[penNameIndex] = {
                      ...dataClone.data[penNameIndex],
                      ...updateDate,
                    }

                    return dataClone
                  }
                  return oldData
                }
              )
              await queryClient.refetchQueries([
                'pen-name-options-by-user',
                user.id,
              ])
              alert.success('แก้ไขนามปากกาสำเร็จ')
            }
          } catch (_) {
            alert.error('เกิดข้อผิดพลาด ไม่สามารถแก้ไขนามปากกาได้')
          } finally {
            editPenNameModal.hide()
          }
        },
      })
    }

    function handleDeletePenName(penNameId: number) {
      deletePenNameModal.show({
        onSubmit: async () => {
          try {
            await penNameClient.removePenName(penNameId)
            alert.success('ลบนามปากกาสำเร็จ')
            await refetch()
            await queryClient.refetchQueries([
              'pen-name-options-by-user',
              user.id,
            ])
            deletePenNameModal.hide()
          } catch (_) {
            errorPenNameModal.show()
          }
        },
      })
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation(
        ({ bookId }: { penNameId: number; bookId: number }) =>
          bookClient.toggleBookLike(bookId),
        {
          onSuccess: (type, { penNameId, bookId }) => {
            queryClient.setQueryData<PenNameByUserResponse | undefined>(
              queryKey,
              (oldData?: PenNameByUserResponse) => {
                if (oldData) {
                  const dataClone = cloneDeep(oldData)
                  const penNameIndex = dataClone.data.findIndex(
                    item => item.id === penNameId
                  )
                  const { books } = dataClone.data[penNameIndex]
                  const bookIndex = books.findIndex(item => item.id === bookId)
                  dataClone.data[penNameIndex].books[bookIndex].isLiked =
                    type === LikeSuccessStatusEnum.CREATE
                  dataClone.data[penNameIndex].books[bookIndex].likeCount =
                    type === LikeSuccessStatusEnum.CREATE
                      ? books[bookIndex].likeCount + 1
                      : Math.max(0, books[bookIndex].likeCount - 1)

                  return dataClone
                }
                return oldData
              }
            )
          },
        }
      )

    async function handleLikeBook(penNameId: number, bookId: number) {
      try {
        await toggleBookLike({ penNameId, bookId })
      } catch (_) {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#my-profile')
    }

    useEffect(() => {
      if (penName)
        handleTabNameChange(
          'penName',
          `นามปากกา (${penName.totalPenName.toLocaleString()})`
        )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [penName])

    const componentProps = {
      userId: user.id,
      penNameList: penName?.data ?? [],
      page,
      perpage,
      total: penName?.total || 0,
      isLoading,
      isLoadingToggleBookLike,
      handlePaginationChange,
      handleSearchChange,
      handleAddPenName,
      handleEditPenName,
      handleDeletePenName,
      handleLikeBook,
    }
    return <Component {...componentProps} />
  }
  return WithProfilePenName
}
